import React from 'react';
import { DoubleCircle } from 'components/MultiCircleGraph/MultiCircleGraph';
import 'partial/OfferManager/modules/ReduceCreditUsageWithLimitIncrease/ReduceCreditUsageWithLimitIncreaseView.scss';
import { ReactComponent as ArrowRight } from 'style/icons/arrow-right.svg';
import SectionContainer from 'components/Containers/SectionContainer';
import { PageContainer } from 'components/Containers/PageContainer/PageContainer';
import { CSTooltip } from 'components/Tooltip/CSToolTip';
import { Row } from 'components/Containers/Row/Row';
import { getUsageStrokeColor } from 'components/Fragments/CreditUsageCircleGraph/CreditUsageCircleGraph';
import { CREDIT_USAGE_ACTION } from 'types/morph.pagePosition.constants';
import TextFromConfig from 'components/TextFromConfig';
import { formatIncome } from 'helpers/money.helper';
import { Disclaimer } from 'components/AdvertiserToolTip/AdvertiserToolTip';
import { useConfig } from 'hook/config.hook';
import UnifiedCCOffersContainer from 'partial/OfferManager/UnifiedCCOffersContainer';
import { FlexContainer } from 'components/Containers/FlexContainer/FlexContainer';

export const capCreditUsage = usage => {
  if (usage > 200) {
    return '>200';
  }
  return usage;
};

const ReduceCreditUsageWithLimitIncreaseView = props => {
  const isCreditUsageValid =
    typeof props?.creditUsage === 'number' && !Number.isNaN(props?.creditUsage);
  const finalCreditUsagePercent = isCreditUsageValid ? props?.creditUsage : 0.0;
  const finalCreditUsageText = isCreditUsageValid
    ? `${capCreditUsage(props?.creditUsage)}%`
    : 'N/A';
  const isCurrentOverLimit = finalCreditUsagePercent > 100;

  const isPotentialCreditUsageValid =
    typeof props?.potentialCreditUsage === 'number' &&
    !Number.isNaN(props?.potentialCreditUsage);
  const finalPotentialCreditUsagePercent = isPotentialCreditUsageValid
    ? props?.potentialCreditUsage
    : 0.0;
  const finalPotentialCreditUsageText = isPotentialCreditUsageValid
    ? `${props?.potentialCreditUsage}%`
    : 'N/A';
  const isPotentialOverLimit = finalPotentialCreditUsagePercent > 100;

  const defaultTestId = 'ReduceCreditUsage';
  const strokeFill = getUsageStrokeColor(finalCreditUsagePercent);
  const strokeFill2 = getUsageStrokeColor(finalPotentialCreditUsagePercent);
  const { config } = useConfig();

  return (
    <PageContainer className="reducecreditusage" datatestid={defaultTestId}>
      {props?.seenPixel}
      <Disclaimer
        showPartnerText
        datatestid="reduceCreditUsage"
        className={'margb8 m-marglr8'}
      />
      <SectionContainer datatestid={defaultTestId}>
        <Row
          datatestid={defaultTestId}
          className="reduceCreditUsageHeaderGraph"
        >
          <div className="creditUsageActionHeader">
            <div
              className="reduceCreditUsageTitle"
              data-testid={`${defaultTestId}-title`}
            >
              <TextFromConfig
                path={
                  TextFromConfig.paths
                    .REDUCE_CREDIT_USAGE_WITH_LIMIT_INCREASE_HEADER
                }
              />
            </div>
            <div
              className="reduceCreditUsageMessage m-text-14"
              data-testid={`${defaultTestId}-subHeader`}
            >
              <TextFromConfig
                path={
                  TextFromConfig.paths
                    .REDUCE_CREDIT_USAGE_WITH_LIMIT_INCREASE_PARAGRAPH
                }
              />
            </div>
          </div>
          <div
            data-testid={`${defaultTestId}-cuGraphContainer`}
            className="reducecreditusage-scorebaord-container"
          >
            <div
              data-testid={`${defaultTestId}-scoreboard-left`}
              className="reducecreditusage-scoreboard-left"
            >
              <div
                data-testid={`${defaultTestId}-currentGraphTitle`}
                className="reducecredit-usage-scoreboard-title"
              >
                Current
              </div>
              <DoubleCircle
                defaultTestid={defaultTestId}
                limit={isCurrentOverLimit}
                percent={finalCreditUsagePercent}
                text={finalCreditUsageText}
                strokeFill={strokeFill}
                width={80}
                shortWidth={68}
              />
              <div
                data-testid={`${defaultTestId}-currentCreditLimitTitle`}
                className="reducecredit-usage-scoreboard-creditlimit"
              >
                CREDIT LIMIT
              </div>
              <div
                data-testid={`${defaultTestId}-currentLimitValue`}
                className="reducecredit-usage-scoreboard-amount"
              >
                {formatIncome(props?.creditLimit)}
              </div>
            </div>

            <div className="reducecreditusage-arrow">
              <ArrowRight />
            </div>

            <div
              data-testid={`${defaultTestId}-scoreboard-right`}
              className="reducecreditusage-scoreboard-right"
            >
              <div
                data-testid={`${defaultTestId}-potentialGraphTitle`}
                className="reducecredit-usage-scoreboard-title"
              >
                Potential{' '}
                <CSTooltip
                  title={
                    config?.actions?.reduce_credit_usage_with_limit_increase
                      ?.potential_limit
                  }
                  isInline={true}
                  datatestid={defaultTestId}
                  tooltipName="Potential"
                  pagePosition="Reduce Credit Usage with Limit Increase"
                />
              </div>
              <DoubleCircle
                defaultTestId={defaultTestId}
                limit={isPotentialOverLimit}
                percent={finalPotentialCreditUsagePercent}
                text={finalPotentialCreditUsageText}
                strokeFill={strokeFill2}
                width={80}
                shortWidth={68}
              />
              <div
                data-testid={`${defaultTestId}-potentialCreditLimitTitle`}
                className="reducecredit-usage-scoreboard-creditlimit"
              >
                CREDIT LIMIT
              </div>
              <div
                data-testid={`${defaultTestId}-potentialLimitValue`}
                className="reducecredit-usage-scoreboard-amount"
              >
                {formatIncome(props?.potentialLimit)}
              </div>
            </div>
          </div>
        </Row>
        <FlexContainer className={'flex-column'}>
          <UnifiedCCOffersContainer
            {...props}
            containerType={'REDUCE_CREDIT_USAGE_WITH_LIMIT_INCREASE'}
            pagePosition={CREDIT_USAGE_ACTION}
            datatestid={defaultTestId}
            seeAllLink="/offers/creditcard"
            seeAllText="See More Credit Cards"
            onLinkClickCallback={props?.onLinkClickCallback}
          />
        </FlexContainer>
      </SectionContainer>
    </PageContainer>
  );
};

export default ReduceCreditUsageWithLimitIncreaseView;
